/**
 * ActionBar Component - Bottom action buttons in live room
 * Includes VotingBar (Step 7) and Discussion (Step 8)
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Heart, MessageCircle, ShoppingBag, Share2, Flame, X, Play, Copy, Check, Sparkles, Loader2, ListPlus } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import DropOverlay from './DropOverlay';
import VotingBar from './VotingBar';
import TrackDiscussion from './TrackDiscussion';
import AddToPlaylistButton from './AddToPlaylistButton';
import PromoKitModal from './PromoKitModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ActionBar = ({ room, currentTrack }) => {
  const [showShop, setShowShop] = useState(false);
  const [showVoting, setShowVoting] = useState(false);
  const [showDiscussion, setShowDiscussion] = useState(false);
  const [liked, setLiked] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showPromo, setShowPromo] = useState(false);
  const [copied, setCopied] = useState(false);
  const [postingStory, setPostingStory] = useState(false);
  const [storyPosted, setStoryPosted] = useState(false);
  const [likeCount, setLikeCount] = useState(0);

  const hasTrack = currentTrack?.id;
  const listenerCount = room?.listenerCount ?? (Array.isArray(room?.listeners) ? room.listeners.length : null);

  // Compact number formatting for badges: 1200 -> 1.2k, 1500000 -> 1.5M
  const fmtCount = (n) => {
    if (n == null) return '';
    if (n >= 1000000) return (n / 1000000).toFixed(n % 1000000 === 0 ? 0 : 1) + 'M';
    if (n >= 1000) return (n / 1000).toFixed(n % 1000 === 0 ? 0 : 1) + 'k';
    return String(n);
  };

  // Reset the "added to story" state whenever the playing song changes
  useEffect(() => { setStoryPosted(false); }, [currentTrack?.id]);

  const handleAddStory = async () => {
    const token = localStorage.getItem('token');
    if (!hasTrack) return;
    if (!token) { window.location.href = '/auth'; return; }
    if (postingStory || storyPosted) return;
    setPostingStory(true);
    try {
      await axios.post(`${API_URL}/api/radio/stories/from-track/${currentTrack.id}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStoryPosted(true);
      toast.success('Added to your Story! Live for 24 hours.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not add to your Story.');
    } finally {
      setPostingStory(false);
    }
  };

  // Song shares point to the backend OG page (renders the song cover for crawlers,
  // then redirects humans to the app). Otherwise we share the live room link.
  const buildShareUrl = () =>
    hasTrack
      ? `${API_URL}/api/radio/og/track/${currentTrack.id}`
      : `${window.location.origin}/room/${room?.id}`;

  // Load like status + count for the current track
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!hasTrack) { setLiked(false); setLikeCount(0); return; }
    // Seed from the track payload so the count shows even when logged out,
    // then refine with the authenticated endpoint (also tells us if *I* liked it).
    setLikeCount(currentTrack.like_count || currentTrack.likeCount || 0);
    if (!token) { setLiked(false); return; }
    axios.get(`${API_URL}/api/artists/radio/tracks/${currentTrack.id}/like`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(res => {
      setLiked(!!res.data.liked);
      if (typeof res.data.like_count === 'number') setLikeCount(res.data.like_count);
    }).catch(() => {});
  }, [currentTrack?.id, hasTrack]);

  const handleLike = async () => {
    const token = localStorage.getItem('token');
    if (!hasTrack) return;
    if (!token) { window.location.href = '/auth'; return; }
    const prev = liked;
    const prevCount = likeCount;
    setLiked(!prev); // optimistic
    setLikeCount(Math.max(0, prevCount + (prev ? -1 : 1)));
    try {
      const res = await axios.post(`${API_URL}/api/artists/radio/tracks/${currentTrack.id}/like`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLiked(!!res.data.liked);
      if (typeof res.data.like_count === 'number') setLikeCount(res.data.like_count);
    } catch (err) {
      console.error('Like failed:', err);
      setLiked(prev); // revert
      setLikeCount(prevCount);
    }
  };

  const handleShare = async () => {
    // For a playing track, open the full Promo Kit (smart link, socials, cover card, QR, captions)
    if (hasTrack) { setShowPromo(true); return; }
    const shareUrl = buildShareUrl();
    const shareText = `Check out ${room?.artistName || 'this artist'} on NLT Radio!`;
    if (navigator.share) {
      try {
        await navigator.share({ title: `${room?.artistName} on NLT Radio`, text: shareText, url: shareUrl });
        return;
      } catch (err) {
        if (err.name !== 'AbortError') console.log('Share failed, showing modal');
      }
    }
    setShowShareModal(true);
  };

  const copyToClipboard = async () => {
    const shareUrl = buildShareUrl();
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => {
        setCopied(false);
        setShowShareModal(false);
      }, 1500);
    } catch (err) {
      console.error('Copy failed:', err);
    }
  };

  return (
    <>
      {showPromo && (
        <PromoKitModal open={showPromo} type="track" id={currentTrack?.id} onClose={() => setShowPromo(false)} />
      )}
      <div className={`fixed left-0 right-0 z-40 px-4 ${hasTrack ? 'bottom-44' : 'bottom-24'}`}>
        {/* Live social proof — pulsing listener count */}
        {hasTrack && listenerCount != null && listenerCount > 0 && (
          <div className="flex justify-center mb-2">
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-black/40 backdrop-blur-md border border-white/10" data-testid="live-listeners-pill">
              <span className="w-1.5 h-1.5 rounded-full bg-[#39FF14] animate-pulse" />
              <span className="text-[11px] font-medium text-white/80">{fmtCount(listenerCount)} listening</span>
            </div>
          </div>
        )}
        <div className="flex items-center justify-between max-w-md mx-auto">
          {/* Like Button */}
          <button 
            onClick={handleLike}
            disabled={!hasTrack}
            className={`relative flex flex-col items-center gap-1 transition-colors ${liked ? 'text-[#FF007F]' : 'text-[#A1A1AA] hover:text-white'} ${!hasTrack ? 'opacity-40 cursor-not-allowed' : ''}`}
            data-testid="like-btn"
          >
            <span className={`relative p-1.5 rounded-xl transition-colors ${liked ? 'bg-[#FF007F]/10' : ''}`}>
              <Heart className={`w-6 h-6 transition-transform ${liked ? 'fill-current scale-110 drop-shadow-[0_0_8px_rgba(255,0,127,0.6)]' : ''}`} />
              {likeCount > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-[#FF007F] text-white text-[9px] font-bold flex items-center justify-center leading-none" data-testid="like-count-badge">
                  {fmtCount(likeCount)}
                </span>
              )}
            </span>
            <span className="text-xs">Like</span>
          </button>
          
          {/* Vote Button - Opens voting panel */}
          <button 
            onClick={() => { setShowVoting(!showVoting); setShowDiscussion(false); }}
            className={`flex flex-col items-center gap-1 transition-colors ${showVoting ? 'text-[#FF007F]' : 'text-[#A1A1AA] hover:text-white'}`}
            data-testid="vote-toggle-btn"
          >
            <span className={`p-1.5 rounded-xl transition-colors ${showVoting ? 'bg-[#FF007F]/15' : ''}`}>
              <Flame className="w-6 h-6" />
            </span>
            <span className="text-xs">Vote</span>
          </button>
          
          {/* Chat/Discussion Button */}
          <button 
            onClick={() => { setShowDiscussion(!showDiscussion); setShowVoting(false); }}
            className={`flex flex-col items-center gap-1 transition-colors ${showDiscussion ? 'text-[#00F0FF]' : 'text-[#A1A1AA] hover:text-white'}`}
            data-testid="chat-btn"
          >
            <span className={`p-1.5 rounded-xl transition-colors ${showDiscussion ? 'bg-[#00F0FF]/15' : ''}`}>
              <MessageCircle className="w-6 h-6" />
            </span>
            <span className="text-xs">Chat</span>
          </button>

          {/* Add to Story Button */}
          <button
            onClick={handleAddStory}
            disabled={!hasTrack || postingStory}
            className={`flex flex-col items-center gap-1 transition-colors ${storyPosted ? 'text-[#FF007F]' : 'text-[#A1A1AA] hover:text-white'} ${!hasTrack ? 'opacity-40 cursor-not-allowed' : ''}`}
            data-testid="story-btn"
          >
            <span className={`p-1.5 rounded-xl transition-colors ${storyPosted ? 'bg-[#FF007F]/10' : ''}`}>
              {postingStory
                ? <Loader2 className="w-6 h-6 animate-spin" />
                : <Sparkles className={`w-6 h-6 ${storyPosted ? 'fill-current drop-shadow-[0_0_8px_rgba(255,0,127,0.6)]' : ''}`} />}
            </span>
            <span className="text-xs">{storyPosted ? 'Added' : 'Story'}</span>
          </button>

          {/* Add to Playlist Button */}
          {hasTrack ? (
            <AddToPlaylistButton trackId={currentTrack.id} variant="icon" />
          ) : (
            <button disabled className="flex flex-col items-center gap-1 text-[#A1A1AA] opacity-40 cursor-not-allowed" data-testid="playlist-btn-disabled">
              <span className="p-1.5"><ListPlus className="w-6 h-6" /></span>
              <span className="text-xs">Playlist</span>
            </button>
          )}

          {/* Shop Button */}
          <button 
            onClick={() => setShowShop(true)}
            className="flex flex-col items-center gap-1 text-[#A1A1AA] hover:text-[#39FF14] transition-colors"
            data-testid="shop-btn"
          >
            <span className="p-1.5"><ShoppingBag className="w-6 h-6" /></span>
            <span className="text-xs">Shop</span>
          </button>
          
          {/* Share Button */}
          <button 
            onClick={handleShare}
            className="flex flex-col items-center gap-1 text-[#A1A1AA] hover:text-white transition-colors"
            data-testid="share-btn"
          >
            <span className="p-1.5"><Share2 className="w-6 h-6" /></span>
            <span className="text-xs">Share</span>
          </button>
        </div>
      </div>

      {/* Share Modal */}
      <AnimatePresence>
        {showShareModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-md z-50 flex items-center justify-center p-4"
            onClick={() => setShowShareModal(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="bg-[#12101A] rounded-2xl border border-white/10 p-6 max-w-sm w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-lg font-bold text-white mb-4">{hasTrack ? `Share "${currentTrack.track_title || currentTrack.title || 'Track'}"` : `Share ${room?.artistName}`}</h3>
              <div className="flex items-center gap-2 p-3 bg-white/5 rounded-xl mb-4">
                <input
                  type="text"
                  value={buildShareUrl()}
                  readOnly
                  className="flex-1 bg-transparent text-white text-sm outline-none"
                />
                <button
                  onClick={copyToClipboard}
                  className="p-2 rounded-lg bg-[#FF007F] hover:bg-[#FF007F]/80 transition-colors"
                >
                  {copied ? <Check className="w-4 h-4 text-white" /> : <Copy className="w-4 h-4 text-white" />}
                </button>
              </div>
              <p className="text-[#71717A] text-sm text-center">
                {copied ? 'Link copied!' : 'Copy link to share'}
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Voting Panel */}
      <AnimatePresence>
        {showVoting && (
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 50 }}
            className={`fixed left-0 right-0 z-40 px-4 ${hasTrack ? 'bottom-60' : 'bottom-40'}`}
          >
            <div className="max-w-md mx-auto bg-[#12101A]/95 backdrop-blur-xl rounded-2xl border border-white/10 p-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-white font-semibold text-sm">Rate this track</h4>
                <button 
                  onClick={() => setShowVoting(false)}
                  className="p-1 rounded-full hover:bg-white/10 text-[#71717A] hover:text-white transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              
              {hasTrack ? (
                <VotingBar 
                  trackId={currentTrack.id}
                  initialVoteCounts={currentTrack.vote_counts}
                  initialUserVote={null}
                />
              ) : (
                <div className="text-center py-6">
                  <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-white/5 flex items-center justify-center">
                    <Play className="w-6 h-6 text-[#71717A]" />
                  </div>
                  <p className="text-[#A1A1AA] text-sm">
                    Play a track to vote on it
                  </p>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Track Discussion */}
      <AnimatePresence>
        {showDiscussion && (
          <TrackDiscussion
            trackId={currentTrack?.id || room?.tracks?.[0]?.id}
            trackTitle={currentTrack?.track_title || room?.tracks?.[0]?.track_title || 'Live Room'}
            isOpen={showDiscussion}
            onClose={() => setShowDiscussion(false)}
            noTrack={!currentTrack?.id && !room?.tracks?.[0]?.id}
          />
        )}
      </AnimatePresence>

      {/* Shop/Drop Overlay */}
      <DropOverlay 
        visible={showShop}
        onClose={() => setShowShop(false)}
        artistName={room?.artistName}
        artistId={room?.artistUserId}
      />
    </>
  );
};

export default ActionBar;
